/**
 * DB Manager: multi-engine database browser and query tool. Compiled in and
 * registered from bundled/index.js like every other first-party extension —
 * never shipped through a marketplace. (It was dev-side once, and described
 * itself as a "reference third-party-style extension" long after that stopped
 * being true.)
 *
 * HOST-RENDERED, like the other six built-ins, as of 05.08.2026 — with one
 * caveat the other six do not have, stated here rather than left for the next
 * reader to discover:
 *
 *   Its results grid, query editor, export and reconnect (DbManagerResultsTab.js)
 *   are first-party host React calling Tauri directly. Its schema tree and
 *   connection dialogs still run in the sandboxed guest iframe (entry.js).
 *   So `hostRendered: true` is a statement about TRUST — first-party code
 *   compiled into this binary, not an installable a user consented to — and not
 *   a claim that no iframe is involved. Porting the tree to the host is a
 *   separate change; the label is not waiting on it, because the trust level is
 *   already what it is.
 *
 * `permissions: []` for the same reason it is empty on ftp/vault/terminal: it
 * means "declares no capabilities for the user to consent to", not "has no
 * access". Its access is real and considerable — three database engines and the
 * secureStorage (the Vault). What it never had was a consent screen or a per-capability
 * toggle: bundled/index.js auto-grants, and Settings offers one switch. Six
 * permission rows implying a control the user does not have was the lie; the
 * list is not where that gets fixed, disclosure is (see SettingsModal.js) and
 * enforcement follows the same rule for every compiled-in extension
 * (capabilities/enforce.js `isCapabilityGateExempt`).
 */
export const dbManagerManifest = {
  schemaVersion: 1,
  id: 'com.apiboo.db-manager',
  name: 'DB Manager',
  version: '0.3.0',
  publisher: 'apiboo',
  entry: 'dist/main.js',
  icon: 'database',
  engines: { apibooSdk: '^1.0.0' },
  hostRendered: true,
  // Was the six-entry list db:mysql / db:postgres / db:sqlite / secureStorage /
  // ui:modal / ui:passwordField. Nothing about the reach changed. What goes with
  // them is the last of the permission UI: those rows lost their switches on
  // 03.08.2026 and became a read-only list of capability strings, and a built-in
  // does not carry one of those — vault, ftp and terminal have at least as much
  // reach and show nothing. If DB Manager should DISCLOSE its access in Settings
  // then so should they, and that is a decision about built-ins in general, not
  // a permission list for this one.
  permissions: [],
  contributes: {
    activityBar: [
      { id: 'db-manager.activity', title: 'DB Manager', icon: 'database', order: { after: 'teams' } },
    ],
    sidebarSections: [
      {
        id: 'db-manager.sidebar',
        activityBarId: 'db-manager.activity',
        title: 'Databases',
        // Icon for this section's EMPTY STATE (the tree itself carries an icon
        // per node). Declared here rather than reused from the activityBar
        // entry because the two answer different questions — the rail icon is
        // the extension's mark, this one describes what the empty tree is
        // missing — and a section is free to differ. Must be a name from the
        // closed allowlist in tree/icons.js; unknown names fall back to Circle.
        icon: 'database',
        headerActions: [
          { id: 'refresh', icon: 'refresh', tooltip: 'Refresh databases' },
          { id: 'add-connection', icon: 'plus', tooltip: 'New connection' },
        ],
      },
    ],
    panels: [{ id: 'db-manager.panel', activityBarId: 'db-manager.activity', title: 'DB Manager' }],
    settings: [],
  },
};
